import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { siteSettingsStore, type SiteSettings } from "@/store/siteSettingsStore";

interface SiteSettingsCtx {
  settings: SiteSettings | null;
  loading: boolean;
}

const SiteSettingsContext = createContext<SiteSettingsCtx>({ settings: null, loading: true });

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const refresh = useCallback(async (silent = false, force = false) => {
    if (!silent) setLoading(true);
    try {
      const s = force ? await siteSettingsStore.getFresh() : await siteSettingsStore.get();
      if (mounted.current) {
        setSettings(s);
        setLoading(false);
      }
    } catch {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;

    // Load immediately
    refresh();

    // Listen to window events
    const handleWindow = () => refresh(true, true);
    window.addEventListener("alsiyahiah:settings:updated", handleWindow);

    // Cross-tab: BroadcastChannel
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("alsiyahiah-settings");
      bc.onmessage = (ev) => {
        if (ev.data?.type === "settings-updated") refresh(true, true);
      };
    } catch (_) { /* ignore */ }

    // Cross-tab: localStorage fallback
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "alsiyahiah-settings-ts") refresh(true, true);
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      mounted.current = false;
      window.removeEventListener("alsiyahiah:settings:updated", handleWindow);
      window.removeEventListener("storage", handleStorage);
      if (bc) bc.close();
    };
  }, [refresh]);

  return (
    <SiteSettingsContext.Provider value={{ settings, loading }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettingsFromContext() {
  return useContext(SiteSettingsContext);
}
